import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  effect,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { NeoAutocompleteV2Module } from '@neogov/pattern-library/neo-autocomplete-v2';
import { NeoFormFieldModule } from '@neogov/pattern-library/neo-form-field';
import { NeoInputModule } from '@neogov/pattern-library/neo-input';
import { FieldErrorsComponent } from '../field-errors/field-errors.component';
import { IconComponent } from '../icon/icon.component';

/**
 * Autocomplete selection event for multi-select autocomplete
 */
export interface MultiSelectAutocompleteEvent<T> {
  option: {
    value: T;
  };
}

/**
 * Multi-Select Autocomplete Field Component
 *
 * A multi-select autocomplete field with search functionality and chip display.
 * Supports lazy loading, custom display functions, and validation.
 *
 * @example
 * ```html
 * <cm-multiselect-autocomplete-field
 *   label="Watchers"
 *   placeholder="Search employees..."
 *   [options]="employees()"
 *   [loading]="loadingEmployees()"
 *   [displayFn]="employee => `${employee.firstName} ${employee.lastName}`"
 *   [trackByFn]="employee => employee.userId"
 *   [required]="true"
 *   autId="watchers-autocomplete"
 *   formControlName="watcherIds"
 *   (searchChange)="onEmployeeSearch($event)"
 *   (itemAdded)="onWatcherAdded($event)"
 *   (itemRemoved)="onWatcherRemoved($event)"
 * >
 *   <ng-template #selectedItemTemplate let-item let-remove="remove">
 *     <div>{{ item.firstName }} {{ item.lastName }}</div>
 *   </ng-template>
 * </cm-multiselect-autocomplete-field>
 * ```
 *
 * @template T - The type of option items
 */
@Component({
  selector: 'cm-multiselect-autocomplete-field',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NeoFormFieldModule,
    NeoInputModule,
    NeoAutocompleteV2Module,
    IconComponent,
    FieldErrorsComponent,
  ],
  templateUrl: './multiselect-autocomplete-field.component.html',
  styleUrl: './multiselect-autocomplete-field.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiselectAutocompleteFieldComponent),
      multi: true,
    },
  ],
})
export class MultiselectAutocompleteFieldComponent<T> implements ControlValueAccessor {
  /**
   * Label displayed above the field
   */
  public readonly label = input.required<string>();

  /**
   * Placeholder text
   */
  public readonly placeholder = input<string>('Search...');

  /**
   * Available options to select from
   */
  public readonly options = input<T[]>([]);

  /**
   * Whether options are loading
   */
  public readonly loading = input<boolean>(false);

  /**
   * Function to get display text for an option
   */
  public readonly displayFn = input.required<(option: T) => string>();

  /**
   * Function to get unique identifier for tracking
   */
  public readonly trackByFn = input.required<(option: T) => string | number>();

  /**
   * Whether the field is required
   */
  public readonly required = input<boolean>(false);

  /**
   * Hint text displayed below the field
   */
  public readonly hint = input<string>('');

  /**
   * Automation test ID
   */
  public readonly autId = input.required<string>();

  /**
   * Custom error messages
   */
  public readonly errorMessages = input<Partial<Record<string, string>>>({});

  /**
   * When to show error messages
   */
  public readonly showErrorsWhen = input<'touched' | 'dirty' | 'always'>('touched');

  /**
   * Whether to hide error messages
   */
  public readonly hideErrors = input<boolean>(false);

  /**
   * Emitted when search input changes
   */
  public readonly searchChange = output<string>();

  /**
   * Emitted when an item is added to selection
   */
  public readonly itemAdded = output<T>();

  /**
   * Emitted when an item is removed from selection
   */
  public readonly itemRemoved = output<T>();

  /**
   * Emitted when the selected items change (full array)
   */
  public readonly selectedItemsChange = output<T[]>();

  /**
   * Pre-selected items (for initialization)
   */
  public readonly preselectedItems = input<T[]>([]);

  /**
   * Template for rendering selected items
   */
  public readonly selectedItemTemplate = contentChild<TemplateRef<{ $implicit: T; remove: () => void }>>('selectedItemTemplate');

  /**
   * Template for rendering option items in the dropdown
   */
  public readonly optionItemTemplate = contentChild<TemplateRef<{ $implicit: T }>>('optionItemTemplate');

  /**
   * Reference to the input element
   */
  public readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputRef');

  /**
   * Current search value
   */
  public searchValue = signal<string>('');

  /**
   * Currently selected options
   */
  public selectedOptions = signal<T[]>([]);

  /**
   * Whether the component is disabled
   */
  public disabled = signal<boolean>(false);

  /**
   * Filtered options excluding already selected items
   */
  public readonly filteredOptions = computed(() => {
    const selectedIds = new Set(this.selectedOptions().map((opt) => this.trackByFn()(opt)));
    return this.options().filter((opt) => !selectedIds.has(this.trackByFn()(opt)));
  });

  /**
   * Selected options with their label, track key and remove callback already resolved, so the
   * template never calls `displayFn()`/`trackByFn()`/`createRemoveFn()` on every change detection cycle.
   */
  protected readonly selectedOptionVms = computed(() =>
    this._toOptionVms(this.selectedOptions()).map((vm) => ({ ...vm, remove: this.createRemoveFn(vm.option) }))
  );

  /**
   * Filtered options with their label and track key already resolved.
   */
  protected readonly filteredOptionVms = computed(() => this._toOptionVms(this.filteredOptions()));

  private _toOptionVms(options: T[]): { option: T; key: string | number; label: string }[] {
    const displayFn = this.displayFn();
    const trackByFn = this.trackByFn();
    return options.map((option) => ({ option, key: trackByFn(option), label: displayFn(option) }));
  }

  private readonly _injector = inject(Injector);
  public readonly ngControl = signal<NgControl | null>(null);

  /**
   * Stores the last written value for re-matching when options change
   */
  private _lastWrittenValue = signal<(string | number)[] | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onChange: (value: (string | number)[] | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched: () => void = () => {};

  constructor() {
    queueMicrotask(() => {
      try {
        const ngControl = this._injector.get(NgControl, null, { optional: true, self: true }) || null;
        this.ngControl.set(ngControl);
      } catch {
        this.ngControl.set(null);
      }
    });

    // Initialize with preselected items
    effect(() => {
      const preselected = this.preselectedItems();
      if (preselected.length > 0 && this.selectedOptions().length === 0) {
        this.selectedOptions.set([...preselected]);
      }
    });

    // Re-match selected options whenever options OR lastWrittenValue change
    effect(() => {
      const options = this.options();
      const lastValue = this._lastWrittenValue();

      if (Array.isArray(lastValue) && lastValue.length > 0 && options.length > 0) {
        const selectedOpts = options.filter((opt) => lastValue.includes(this.trackByFn()(opt)));
        this.selectedOptions.set(selectedOpts);
      } else if (lastValue === null || lastValue === undefined || (Array.isArray(lastValue) && lastValue.length === 0)) {
        // Don't clear if we have preselected items
        if (this.preselectedItems().length === 0) {
          this.selectedOptions.set([]);
        }
      }
    });
  }

  public writeValue(value: (string | number)[] | null): void {
    // Store the value for re-matching when options load
    this._lastWrittenValue.set(value);

    if (Array.isArray(value) && value.length > 0) {
      const selectedOpts = this.options().filter((opt) => value.includes(this.trackByFn()(opt)));
      this.selectedOptions.set(selectedOpts);
    } else {
      this.selectedOptions.set([]);
    }
  }

  public registerOnChange(fn: (value: (string | number)[] | null) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  /**
   * Handles search input changes
   */
  public onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.searchValue.set(value);
    this.searchChange.emit(value);
  }

  /**
   * Handles option selection - adds to the selected items
   */
  public onOptionSelected(event: MultiSelectAutocompleteEvent<T> | null | undefined): void {
    if (!event || !event.option || event.option.value === undefined) {
      return;
    }

    const option = event.option.value;
    const currentSelection = [...this.selectedOptions()];
    const optionId = this.trackByFn()(option);

    // Check if already selected
    if (!currentSelection.some((opt) => this.trackByFn()(opt) === optionId)) {
      currentSelection.push(option);
      this.selectedOptions.set(currentSelection);

      const values = currentSelection.map((opt) => this.trackByFn()(opt));
      this._onChange(values);
      this._onTouched();

      this.itemAdded.emit(option);
      this.selectedItemsChange.emit(currentSelection);
    }

    // Clear search
    this.searchValue.set('');

    // Manually clear the input element value to prevent neo-autocomplete from setting it
    setTimeout(() => {
      const input = this.inputElement();
      if (input?.nativeElement) {
        input.nativeElement.value = '';
      }
    }, 0);
  }

  /**
   * Handles input blur
   */
  public onInputBlur(): void {
    this._onTouched();
  }

  /**
   * Removes a selected option
   */
  public onRemoveOption(option: T): void {
    const currentSelection = this.selectedOptions();
    const optionId = this.trackByFn()(option);
    const newSelection = currentSelection.filter((opt) => this.trackByFn()(opt) !== optionId);

    this.selectedOptions.set(newSelection);

    const values = newSelection.map((opt) => this.trackByFn()(opt));
    this._onChange(values.length > 0 ? values : null);
    this._onTouched();

    this.itemRemoved.emit(option);
    this.selectedItemsChange.emit(newSelection);
  }

  /**
   * Creates a remove function bound to a specific option
   */
  public createRemoveFn(option: T): () => void {
    return () => this.onRemoveOption(option);
  }
}
